import type { ProjectResponseDto, SceneResponseDto } from 'trace-viewer/cms';
import { resolveUrl } from './url';

function escapeXml(str: string): string {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

/** Build a shareable URL with ?projects= query for the given projects. */
export function buildShareUrl(projects: ProjectResponseDto[], basePath = '/map'): string {
    const codes = projects
        .map(p => p.shortCode ?? p.id)
        .filter(Boolean)
        .join(',');
    return `${window.location.origin}${basePath}?projects=${codes}`;
}

function buildScenePlacemark(scene: SceneResponseDto, styleId: string): string {
    const acc = scene.sceneAccessors?.[0];
    if (acc?.latitude == null || acc?.longitude == null) return '';
    const name = escapeXml(scene.name ?? 'Untitled Scene');
    const desc = scene.description ? `\n        <description>${escapeXml(scene.description)}</description>` : '';
    const shortCode = scene.shortCode;
    const viewerUrl = shortCode ? `${window.location.origin}/${shortCode}` : '';
    const extData = viewerUrl ? `\n        <ExtendedData>
          <Data name="viewerUrl"><value>${escapeXml(viewerUrl)}</value></Data>
        </ExtendedData>` : '';
    return `      <Placemark>
        <name>${name}</name>${desc}
        <styleUrl>#${styleId}</styleUrl>${extData}
        <Point>
          <coordinates>${acc.longitude},${acc.latitude},0</coordinates>
        </Point>
      </Placemark>`;
}

function buildSceneStyle(scene: SceneResponseDto, id: string): string {
    const imageUrl = resolveUrl(scene.imageUrl);
    if (!imageUrl) return '';
    return `    <Style id="${id}">
      <IconStyle>
        <Icon><href>${escapeXml(imageUrl)}</href></Icon>
        <hotSpot x="0.5" y="0.5" xunits="fraction" yunits="fraction"/>
        <scale>1.0</scale>
      </IconStyle>
    </Style>`;
}

/**
 * Generate a KML document with projects as folders, each containing its scenes.
 * Projects and scenes get custom icons from their image URLs.
 */
export function downloadKml(projects: ProjectResponseDto[], filename = 'trace-projects.kml') {
    const styles: string[] = [];
    const folders: string[] = [];

    for (const project of projects) {
        const projectAcc = project.projectAccessors?.[0];
        const projectName = escapeXml(project.name ?? 'Untitled Project');
        const projectDesc = project.description ? `\n      <description>${escapeXml(project.description)}</description>` : '';

        // Project icon style
        const projectStyleId = escapeXml(`project-${project.id ?? 'unknown'}`);
        const projectIconUrl = resolveUrl(project.logoUrl ?? project.graphicUrl);
        if (projectIconUrl) {
            styles.push(`    <Style id="${projectStyleId}">
      <IconStyle>
        <Icon><href>${escapeXml(projectIconUrl)}</href></Icon>
        <hotSpot x="0.5" y="0.5" xunits="fraction" yunits="fraction"/>
        <scale>1.2</scale>
      </IconStyle>
    </Style>`);
        }

        // Project placemark (if it has coordinates)
        let projectPlacemark = '';
        if (projectAcc?.latitude != null && projectAcc?.longitude != null) {
            const shortCode = project.shortCode;
            const shareUrl = shortCode ? `${window.location.origin}/map?projects=${shortCode}` : '';
            const extData = shareUrl ? `\n        <ExtendedData>
          <Data name="shareUrl"><value>${escapeXml(shareUrl)}</value></Data>
        </ExtendedData>` : '';
            projectPlacemark = `      <Placemark>
        <name>${projectName}</name>${projectDesc}
        ${projectIconUrl ? `<styleUrl>#${projectStyleId}</styleUrl>` : ''}${extData}
        <Point>
          <coordinates>${projectAcc.longitude},${projectAcc.latitude},0</coordinates>
        </Point>
      </Placemark>`;
        }

        // Scene placemarks
        const scenes = project.scenes ?? [];
        const scenePlacemarks: string[] = [];
        for (let i = 0; i < scenes.length; i++) {
            const scene = scenes[i];
            const sceneStyleId = escapeXml(`scene-${scene.id ?? i}`);
            const style = buildSceneStyle(scene, sceneStyleId);
            if (style) styles.push(style);
            const placemark = buildScenePlacemark(scene, sceneStyleId);
            if (placemark) scenePlacemarks.push(placemark);
        }

        // Build folder
        const folderContent = [projectPlacemark, ...scenePlacemarks].filter(Boolean).join('\n');
        if (folderContent) {
            folders.push(`    <Folder>
      <name>${projectName}</name>${projectDesc}
      <open>1</open>
${folderContent}
    </Folder>`);
        }
    }

    const kml = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>Trace Projects</name>
${styles.join('\n')}
${folders.join('\n')}
  </Document>
</kml>`;

    const blob = new Blob([kml], { type: 'application/vnd.google-earth.kml+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    // Firefox requires the anchor to be in the document for click() to trigger download
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Defer revoke so the download has time to start (Chrome aborts otherwise)
    setTimeout(() => URL.revokeObjectURL(url), 100);
}
